import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { useAuth } from '@/lib/auth';
import { Bell, Plus, Edit2, Trash2, AlertCircle, CheckCircle, Eye } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { zhCN } from 'date-fns/locale';

interface Notification {
  id: string;
  title: string;
  content: string;
  type: 'general' | 'system' | 'book' | 'announcement';
  priority: 0 | 1 | 2;
  targetAudience: 'all' | 'users' | 'admins';
  maxLevel?: number | null;
  isActive: boolean;
  createdBy: string;
  createdByName?: string;
  createdAt: string;
  updatedAt: string;
  expiresAt?: string;
  readCount: number;
}

const typeOptions = [
  { value: 'general', label: '普通通知' },
  { value: 'system', label: '系统通知' },
  { value: 'book', label: '书籍相关' },
  { value: 'announcement', label: '公告' }
];

const audienceOptions = [
  { value: 'all', label: '所有用户' },
  { value: 'users', label: '普通用户' },
  { value: 'admins', label: '管理员' },
  { value: 'level', label: '按等级（<= 阈值）' }
];

const audienceFilterOptions = [
  { value: 'all', label: '所有用户' },
  { value: 'users', label: '普通用户' },
  { value: 'admins', label: '管理员' },
];

const priorityLabels = {
  0: { label: '普通', color: 'bg-gray-100 text-gray-700' },
  1: { label: '重要', color: 'bg-yellow-100 text-yellow-700' },
  2: { label: '紧急', color: 'bg-red-100 text-red-700' }
};

const typeLabels = {
  general: { label: '普通', icon: Bell },
  system: { label: '系统', icon: AlertCircle },
  book: { label: '书籍', icon: Eye },
  announcement: { label: '公告', icon: CheckCircle }
};

function getErrorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err && typeof (err as { message: unknown }).message === 'string') {
    return (err as { message: string }).message;
  }
  return String(err);
}

export function NotificationsPage() {
  const { token } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingNotification, setEditingNotification] = useState<Notification | null>(null);
  const [deletingNotification, setDeletingNotification] = useState<Notification | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    type: 'general' as Notification['type'],
    priority: 0 as Notification['priority'],
    targetAudience: 'all' as Notification['targetAudience'] | 'level',
    maxLevel: '' as string,
    expiresAt: '',
    isActive: true
  });
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [filter, setFilter] = useState({
    type: '',
    targetAudience: '',
    isActive: ''
  });

  useEffect(() => {
    loadNotifications();
  }, [filter]);

  async function loadNotifications() {
    try {
      const params = new URLSearchParams();
      if (filter.type) params.append('type', filter.type);
      if (filter.targetAudience) params.append('targetAudience', filter.targetAudience);
      if (filter.isActive) params.append('isActive', filter.isActive);

      const response = await fetch(`/api/admin/notifications?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) throw new Error('Failed to load notifications');
      const data = await response.json();
      setNotifications(data.items || []);
    } catch (err) {
      setError('加载通知失败');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate() {
    try {
      setError('');
      const payload: Omit<typeof formData, 'targetAudience' | 'maxLevel'> & {
        targetAudience: Notification['targetAudience']
        maxLevel: number | null
      } = {
        ...formData,
        targetAudience: formData.targetAudience as Notification['targetAudience'],
        maxLevel: null,
      };
      if (formData.targetAudience !== 'level') {
        payload.maxLevel = null;
      } else {
        payload.targetAudience = 'users';
        const parsed = Number(formData.maxLevel);
        if (!Number.isFinite(parsed) || parsed < 0) {
          throw new Error('等级阈值必须是非负整数');
        }
        payload.maxLevel = Math.floor(parsed);
      }
      const response = await fetch('/api/admin/notifications', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create notification');
      }

      await loadNotifications();
      setShowCreateModal(false);
      resetForm();
      setSuccessMessage('通知创建成功');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    }
  }

  async function handleUpdate() {
    if (!editingNotification) return;

    try {
      setError('');
      const payload: Omit<typeof formData, 'targetAudience' | 'maxLevel'> & {
        targetAudience: Notification['targetAudience']
        maxLevel: number | null
      } = {
        ...formData,
        targetAudience: formData.targetAudience as Notification['targetAudience'],
        maxLevel: null,
      };
      if (formData.targetAudience !== 'level') {
        payload.maxLevel = null;
      } else {
        payload.targetAudience = 'users';
        const parsed = Number(formData.maxLevel);
        if (!Number.isFinite(parsed) || parsed < 0) {
          throw new Error('等级阈值必须是非负整数');
        }
        payload.maxLevel = Math.floor(parsed);
      }
      const response = await fetch(`/api/admin/notifications/${editingNotification.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update notification');
      }

      await loadNotifications();
      setEditingNotification(null);
      resetForm();
      setSuccessMessage('通知更新成功');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    }
  }

  async function confirmDelete(notification: Notification) {
    setDeletingNotification(notification);
    setError('');
  }

  async function handleDelete() {
    if (!deletingNotification) return;

    try {
      setError('');
      const response = await fetch(`/api/admin/notifications/${deletingNotification.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || '删除失败');
      }

      await loadNotifications();
      setSuccessMessage(`通知"${deletingNotification.title}"已删除`);
      setDeletingNotification(null);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    }
  }

  function startEdit(notification: Notification) {
    setEditingNotification(notification);
    setFormData({
      title: notification.title,
      content: notification.content,
      type: notification.type,
      priority: notification.priority,
      targetAudience: notification.maxLevel !== null && notification.maxLevel !== undefined ? 'level' : notification.targetAudience,
      maxLevel: notification.maxLevel !== null && notification.maxLevel !== undefined ? String(notification.maxLevel) : '',
      expiresAt: notification.expiresAt || '',
      isActive: notification.isActive
    });
  }

  function resetForm() {
    setFormData({
      title: '',
      content: '',
      type: 'general',
      priority: 0,
      targetAudience: 'all',
      maxLevel: '',
      expiresAt: '',
      isActive: true
    });
    setError('');
  }

  function parseApiDate(value: string) {
    // Fallback for legacy API payloads: "YYYY-MM-DD HH:MM:SS" should be treated as UTC.
    const normalized = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value)
      ? `${value.replace(' ', 'T')}Z`
      : value;
    return new Date(normalized);
  }

  function formatDateTime(dateString: string) {
    return formatDistanceToNow(parseApiDate(dateString), { addSuffix: true, locale: zhCN });
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">加载中...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 p-8 text-white shadow-2xl">
        <div className="absolute inset-0 bg-black/5" />
        <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute -bottom-20 -left-20 h-64 w-64 rounded-full bg-white/10 blur-3xl" />

        <div className="relative flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/20 px-4 py-2 text-xs font-semibold uppercase tracking-wider backdrop-blur-sm">
              <Bell className="h-4 w-4" /> 通知管理中心
            </div>
            <h1 className="text-3xl font-bold lg:text-4xl">通知管理</h1>
            <p className="max-w-3xl text-sm text-white/90 lg:text-base">
              创建和管理系统通知，向用户发布重要信息和公告
            </p>
          </div>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="rounded-2xl bg-white/15 p-6 text-center backdrop-blur-md">
              <p className="text-3xl font-bold">{notifications.length}</p>
              <p className="mt-1 text-sm text-white/80">通知总数</p>
            </div>
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm shadow-xl border border-white/30"
              size="lg"
            >
              <Plus className="mr-2 h-4 w-4" />创建通知
            </Button>
          </div>
        </div>
      </div>

      {/* 筛选器 */}
      <Card className="border-gray-100 shadow-xl">
        <CardHeader>
          <CardTitle className="text-lg font-bold text-gray-900">筛选器</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <select
              value={filter.type}
              onChange={(e) => setFilter({ ...filter, type: e.target.value })}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm"
            >
              <option value="">所有类型</option>
              {typeOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <select
              value={filter.targetAudience}
              onChange={(e) => setFilter({ ...filter, targetAudience: e.target.value })}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm"
            >
              <option value="">所有受众</option>
              {audienceFilterOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <select
              value={filter.isActive}
              onChange={(e) => setFilter({ ...filter, isActive: e.target.value })}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm"
            >
              <option value="">所有状态</option>
              <option value="true">激活</option>
              <option value="false">未激活</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* 通知列表 */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                通知信息
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                类型
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                优先级
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                目标受众
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                状态
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                阅读数
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                创建时间
              </th>
              <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                操作
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {notifications.map((notification) => {
              const IconComponent = typeLabels[notification.type].icon;
              const priorityStyle = priorityLabels[notification.priority];

              return (
                <tr key={notification.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-start">
                      <div className={`p-2 rounded-lg mr-3 ${priorityStyle.color}`}>
                        <IconComponent className="h-4 w-4" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {notification.title}
                        </p>
                        <p className="text-sm text-gray-500 truncate max-w-xs mt-1">
                          {notification.content}
                        </p>
                        {notification.expiresAt && (
                          <p className="text-xs text-orange-600 mt-1">
                            过期: {parseApiDate(notification.expiresAt).toLocaleDateString('zh-CN')}
                          </p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {typeLabels[notification.type].label}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${priorityStyle.color}`}>
                      {priorityStyle.label}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {notification.maxLevel !== null && notification.maxLevel !== undefined
                      ? `等级 <= ${notification.maxLevel} 可见`
                      : audienceOptions.find(a => a.value === notification.targetAudience)?.label}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      notification.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {notification.isActive ? '激活' : '未激活'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {notification.readCount}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDateTime(notification.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => startEdit(notification)}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => confirmDelete(notification)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {notifications.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          暂无通知
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="fixed top-4 right-4 bg-green-50 text-green-600 p-4 rounded-lg shadow-lg z-50 animate-fade-in">
          {successMessage}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingNotification && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in">
          <div className="bg-white rounded-2xl p-8 w-full max-w-md shadow-2xl transform transition-all">
            <h2 className="text-xl font-bold mb-4 text-red-600">
              确认删除通知
            </h2>
            <p className="mb-4">
              确定要删除通知 "<strong>{deletingNotification.title}</strong>" 吗？
            </p>
            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded mb-4">
                {error}
              </div>
            )}
            <div className="flex gap-2">
              <Button
                variant="danger"
                onClick={handleDelete}
              >
                确认删除
              </Button>
              <Button
                variant="secondary"
                onClick={() => setDeletingNotification(null)}
              >
                取消
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || editingNotification) && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in">
          <div className="bg-white rounded-2xl p-8 w-full max-w-2xl shadow-2xl transform transition-all max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              {editingNotification ? '编辑通知' : '创建新通知'}
            </h2>
            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded mb-4">
                {error}
              </div>
            )}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  标题 *
                </label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="输入通知标题"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  内容 *
                </label>
                <Textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="输入通知内容"
                  rows={6}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">类型</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value as Notification['type'] })}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2"
                  >
                    {typeOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">优先级</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) as Notification['priority'] })}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2"
                  >
                    <option value={0}>普通</option>
                    <option value={1}>重要</option>
                    <option value={2}>紧急</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">目标受众</label>
                  <select
                    value={formData.targetAudience}
                    onChange={(e) => setFormData({ ...formData, targetAudience: e.target.value as Notification['targetAudience'] | 'level' })}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2"
                  >
                    {audienceOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  {formData.targetAudience === 'level' && (
                    <div className="mt-2">
                      <Input
                        type="number"
                        min={0}
                        step={1}
                        value={formData.maxLevel}
                        onChange={(e) => setFormData({ ...formData, maxLevel: e.target.value })}
                        placeholder="例如：3（等级 0~3 可见）"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        规则：等级越小权限越高，只有等级 &lt;= 阈值的用户能收到
                      </p>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">过期时间（可选）</label>
                  <Input
                    type="datetime-local"
                    value={formData.expiresAt}
                    onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="rounded border-gray-300"
                />
                <label htmlFor="isActive" className="ml-2 text-sm text-gray-700">
                  立即激活此通知
                </label>
              </div>
            </div>
            <div className="flex gap-3 mt-8">
              <Button
                onClick={editingNotification ? handleUpdate : handleCreate}
                disabled={!formData.title.trim() || !formData.content.trim()}
                className="flex-1"
              >
                {editingNotification ? '更新通知' : '创建通知'}
              </Button>
              <Button
                variant="secondary"
                className="flex-1"
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingNotification(null);
                  resetForm();
                }}
              >
                取消
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
